import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '@/config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LinearProgress from '@mui/material/LinearProgress';
import CloseIcon from '@mui/icons-material/Close';
import { usePlan } from '../hooks/usePlan';
import type { PlanType, BillingCycle, PlanFeature } from '../types';



const PLAN_FEATURE_KEYS = [
  { nameKey: 'plans.feat.photos', free: '100', starter: '1.000', explorer: '10.000', premium: '100.000' },
];

const PLAN_PRICES: Record<PlanType, { monthly: number; annual: number }> = {
  free: { monthly: 0, annual: 0 },
  starter: { monthly: 19.9, annual: 190.8 },
  explorer: { monthly: 49.9, annual: 478.8 },
  premium: { monthly: 99.9, annual: 958.8 },
};

const PLAN_LABELS: Record<PlanType, string> = {
  free: 'Free',
  starter: 'Starter',
  explorer: 'Explorer',
  premium: 'Premium',
};

export function PlanUpgradePage(): React.ReactElement {
  const { t } = useTranslation();
  // Handle Stripe redirect with success
  const [searchParams] = useSearchParams();
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  
  useEffect(() => {
    const success = searchParams.get('success');
    const plan = searchParams.get('plan');
    if (success === 'true' && plan) {
      const token = localStorage.getItem('accessToken');
      fetch(`${API_BASE_URL}/api/v1/billing/confirm-upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan }),
      }).then(res => {
        if (res.ok) setUpgradeSuccess(true);
      }).catch(() => {});
    }
  }, [searchParams]);

  const { planInfo, isLoading, error, upgrade } = usePlan();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [upgrading, setUpgrading] = useState<PlanType | null>(null);
  const [downgradeTarget, setDowngradeTarget] = useState<PlanType | null>(null);

  const handleUpgrade = async (plan: PlanType) => {
    setUpgrading(plan);
    try {
      const result = await upgrade({ plan, billingCycle });
      // Redirect to checkout
      window.location.href = result.checkoutUrl;
    } catch (err) {
      // Error is handled by the hook
      setUpgrading(null);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const plans: PlanType[] = ['free', 'starter', 'explorer', 'premium'];

  return (
    <Box p={3} maxWidth="1200px" mx="auto">
      {upgradeSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('plans.upgradeSuccess')}
        </Alert>
      )}

      <Typography variant="h4" gutterBottom fontWeight={700}>
        {t('plans.title')}
      </Typography>

      <Typography variant="body1" color="text.secondary" mb={3}>
        {t('plans.features')}
      </Typography>

      {/* Billing cycle toggle */}
      <Box display="flex" justifyContent="center" mb={4}>
        <ToggleButtonGroup
          value={billingCycle}
          exclusive
          onChange={(_, value) => value && setBillingCycle(value as BillingCycle)}
          aria-label="Billing cycle"
        >
          <ToggleButton value="monthly">{t('plans.monthly')}</ToggleButton>
          <ToggleButton value="annual">
            {t('plans.yearly')}
            <Chip label="-20%" size="small" color="primary" sx={{ ml: 1 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Plan comparison table */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('plans.features')}</TableCell>
              {plans.map((plan) => (
                <TableCell key={plan} align="center" sx={{ fontWeight: 600 }}>
                  <Box>
                    {PLAN_LABELS[plan]}
                    {planInfo?.plan === plan && (
                      <Chip label={t('plans.current')} size="small" color="primary" sx={{ ml: 1 }} />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Price row */}
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('plans.feat.price')}</TableCell>
              {plans.map((plan) => (
                <TableCell key={plan} align="center">
                  {plan === 'free' ? (
                    <Typography variant="body1" fontWeight={700}>{t('plans.feat.free')}</Typography>
                  ) : (
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        R$ {billingCycle === 'monthly'
                          ? PLAN_PRICES[plan].monthly.toFixed(2).replace('.', ',')
                          : (PLAN_PRICES[plan].annual / 12).toFixed(2).replace('.', ',')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        /{t('plans.feat.month')}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
            {/* Feature rows */}
            {PLAN_FEATURE_KEYS.map((feature) => (
              <TableRow key={feature.name}>
                <TableCell>{t(feature.nameKey)}</TableCell>
                {plans.map((plan) => {
                  const value = feature[plan];
                  return (
                    <TableCell key={plan} align="center">
                      {typeof value === 'boolean' ? (
                        value ? (
                          <CheckIcon color="success" fontSize="small" />
                        ) : (
                          <CloseIcon color="disabled" fontSize="small" />
                        )
                      ) : (
                        <Typography variant="body2">{value}</Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {/* Action row */}
            <TableRow>
              <TableCell />
              {plans.map((plan) => {
                const planOrder = ['free', 'starter', 'explorer', 'premium'];
                const quotas: Record<string, number> = { free: 100, starter: 1000, explorer: 10000, premium: 100000 };
                const currentIdx = planOrder.indexOf(planInfo?.plan || 'free');
                const targetIdx = planOrder.indexOf(plan);
                return (
                <TableCell key={plan} align="center">
                  {planInfo?.plan === plan ? (
                    <Button variant="outlined" disabled>{t('plans.current')}</Button>
                  ) : targetIdx > currentIdx ? (
                    <Button variant="contained" color="primary" onClick={() => handleUpgrade(plan)} disabled={upgrading !== null}>
                      {upgrading === plan ? <CircularProgress size={20} color="inherit" /> : t('plans.subscribe')}
                    </Button>
                  ) : targetIdx < currentIdx && planInfo && planInfo.photosUsed <= quotas[plan] ? (
                    <Button variant="outlined" color="secondary" size="small"
                      onClick={async () => {
                        setDowngradeTarget(plan);
                      }}
                      disabled={upgrading !== null}
                    >
                      {upgrading === plan ? <CircularProgress size={20} /> : 'Voltar para ' + PLAN_LABELS[plan]}
                    </Button>
                  ) : null}
                </TableCell>
              );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Current plan info + storage */}
      {planInfo && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={700}>
                {t('plans.currentPlan')}
              </Typography>
              <Chip label={PLAN_LABELS[planInfo.plan]} color="primary" variant="filled" />
            </Box>
            {/* Storage bar */}
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              Armazenamento
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((planInfo.photosUsed / planInfo.photoQuota) * 100, 100)}
                  sx={{ height: 10, borderRadius: 5, bgcolor: '#e3f2fd', '& .MuiLinearProgress-bar': { borderRadius: 5, background: planInfo.photosUsed / planInfo.photoQuota > 0.9 ? '#e53935' : 'linear-gradient(90deg, #42a5f5, #1565c0)' } }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'right' }}>
                {Math.round((planInfo.photosUsed / planInfo.photoQuota) * 100)}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {planInfo.photosUsed.toLocaleString('pt-BR')} de {planInfo.photoQuota.toLocaleString('pt-BR')} fotos utilizadas
              {planInfo.bonusQuota > 0 && ` (inclui ${planInfo.bonusQuota} bônus)`}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Downgrade Confirmation Dialog */}
      <Dialog open={downgradeTarget !== null} onClose={() => setDowngradeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Mudar de plano
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Tem certeza que deseja voltar para o plano <strong>{downgradeTarget ? PLAN_LABELS[downgradeTarget] : ''}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Seu limite de armazenamento será reduzido para {downgradeTarget ? (downgradeTarget === 'free' ? '100' : downgradeTarget === 'starter' ? '1.000' : '10.000') : ''} fotos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDowngradeTarget(null)} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => {
              const plan = downgradeTarget;
              setDowngradeTarget(null);
              if (!plan) return;
              setUpgrading(plan);
              try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`${API_BASE_URL}/api/v1/billing/downgrade`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ plan }),
                });
                if (res.ok) { window.location.reload(); }
              } catch {}
              setUpgrading(null);
            }}
          >
            Confirmar mudança
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}